import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Link as LinkIcon, Edit, Trash2, Package, BarChart3, Palette, Settings } from 'lucide-react';
import CollectionTree from '@/components/CollectionTree';

export default function ProductManager() {
  const navigate = useNavigate();
  const { storeId } = useParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [departments, setDepartments] = useState([]);  // Keep for backward compatibility
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Add/Edit Product Dialog
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    title: '',
    description: '',
    original_url: '',
    affiliate_link: '',
    images: [],
    collection_ids: [],
    recommendation: '',
  });
  const [scraping, setScraping] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Add/Edit Collection Dialog
  const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionParent, setNewCollectionParent] = useState(null);
  
  useEffect(() => {
    fetchData();
  }, [storeId]);
  
  useEffect(() => {
    if (store) {
      document.title = `Products - ${store.name}`;
    }
  }, [store]);
  
  const fetchData = async () => {
    try {
      const url = selectedCollection 
        ? `${API}/stores/${storeId}/products?collection_id=${selectedCollection}`
        : `${API}/stores/${storeId}/products`;
        
      const [storeRes, productsRes, collectionsRes, deptsRes] = await Promise.all([
        fetch(`${API}/stores/${storeId}`, { credentials: 'include' }),
        fetch(url, { credentials: 'include' }),
        fetch(`${API}/stores/${storeId}/collections`, { credentials: 'include' }),
        fetch(`${API}/stores/${storeId}/departments`, { credentials: 'include' }),
      ]);
      
      if (!storeRes.ok) throw new Error('Store not found');
      
      const storeData = await storeRes.json();
      const productsData = await productsRes.json();
      const collectionsData = await collectionsRes.json();
      const deptsData = await deptsRes.json();
      
      setStore(storeData);
      setProducts(productsData);
      setCollections(collectionsData);
      setDepartments(deptsData);
    } catch (error) {
      toast.error('Failed to load data');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  // Collection Management Functions
  const handleAddCollection = async () => {
    if (!newCollectionName.trim()) {
      toast.error('Collection name is required');
      return;
    }
    
    try {
      if (editingCollection) {
        // Update existing collection
        const response = await fetch(`${API}/collections/${editingCollection.collection_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: newCollectionName }),
        });
        
        if (!response.ok) throw new Error('Failed to update collection');
        toast.success('Collection updated!');
      } else {
        // Create new collection
        const response = await fetch(`${API}/stores/${storeId}/collections`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ 
            name: newCollectionName, 
            parent_id: newCollectionParent,
            order: collections.length
          }),
        });
        
        if (!response.ok) throw new Error('Failed to add collection');
        toast.success('Collection added!');
      }
      
      setCollectionDialogOpen(false);
      setNewCollectionName('');
      setNewCollectionParent(null);
      setEditingCollection(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleDeleteCollection = async (collectionId) => {
    if (!window.confirm('Delete this collection and all subcollections? Products will remain but be uncategorized.')) return;
    
    try {
      const response = await fetch(`${API}/collections/${collectionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error('Failed to delete collection');
      
      toast.success('Collection deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const openEditCollectionDialog = (collection) => {
    setEditingCollection(collection);
    setNewCollectionName(collection.name);
    setNewCollectionParent(collection.parent_id);
    setCollectionDialogOpen(true);
  };
  
  const openAddSubcollectionDialog = (parentId) => {
    setEditingCollection(null);
    setNewCollectionName('');
    setNewCollectionParent(parentId);
    setCollectionDialogOpen(true);
  };
  
  const handleSelectCollection = (collectionId) => {
    setSelectedCollection(collectionId);
    // Refetch products with the new filter
    fetchData();
  };

  
  const handleScrapeProduct = async () => {
    if (!productForm.original_url) {
      toast.error('Please enter a product URL');
      return;
    }
    
    setScraping(true);
    try {
      const response = await fetch(`${API}/products/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: productForm.original_url }),
      });
      
      if (!response.ok) throw new Error('Failed to scrape product');
      
      const scraped = await response.json();
      
      if (scraped.error) {
        toast.error('Failed to scrape: ' + scraped.error);
        return;
      }
      
      setProductForm({
        ...productForm,
        title: scraped.title || productForm.title,
        description: scraped.description || productForm.description,
        images: scraped.images || productForm.images,
        affiliate_link: productForm.original_url,
      });
      
      toast.success('Product data scraped successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setScraping(false);
    }
  };
  
  const handleSubmitProduct = async () => {
    if (!productForm.title || !productForm.original_url || !productForm.affiliate_link) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const url = editingProduct
        ? `${API}/products/${editingProduct.product_id}`
        : `${API}/stores/${storeId}/products`;
      
      const method = editingProduct ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(productForm),
      });
      
      if (!response.ok) throw new Error('Failed to save product');
      
      toast.success(editingProduct ? 'Product updated!' : 'Product added!');
      setProductDialogOpen(false);
      resetProductForm();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const response = await fetch(`${API}/products/${productId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error('Failed to delete product');
      
      toast.success('Product deleted');
      setProducts(products.filter(p => p.product_id !== productId));
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleAddDepartment = async () => {
    if (!newDeptName.trim()) {
      toast.error('Department name is required');
      return;
    }
    
    try {
      if (editingDept) {
        // Update existing department
        const response = await fetch(`${API}/departments/${editingDept.dept_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: newDeptName, order: editingDept.order }),
        });
        
        if (!response.ok) throw new Error('Failed to update department');
        toast.success('Department updated!');
      } else {
        // Create new department
        const response = await fetch(`${API}/stores/${storeId}/departments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: newDeptName, order: departments.length }),
        });
        
        if (!response.ok) throw new Error('Failed to add department');
        toast.success('Department added!');
      }
      
      setDeptDialogOpen(false);
      setNewDeptName('');
      setEditingDept(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleDeleteDepartment = async (deptId) => {
    if (!window.confirm('Delete this department? Products in it will remain but be uncategorized.')) return;
    
    try {
      const response = await fetch(`${API}/departments/${deptId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error('Failed to delete department');
      
      toast.success('Department deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const openEditDeptDialog = (dept) => {
    setEditingDept(dept);
    setNewDeptName(dept.name);
    setDeptDialogOpen(true);
  };
  
  const resetProductForm = () => {
    setProductForm({
      title: '',
      description: '',
      original_url: '',
      affiliate_link: '',
      images: [],
      collection_ids: [],
      recommendation: '',
    });
    setEditingProduct(null);
  };
  
  const openEditDialog = (product) => {
    setEditingProduct(product);
    setProductForm({
      title: product.title,
      description: product.description || '',
      original_url: product.original_url,
      affiliate_link: product.affiliate_link,
      images: product.images || [],
      collection_ids: product.collection_ids || [],
      recommendation: product.recommendation || '',
    });
    setProductDialogOpen(true);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background flex">
      {/* Collection Tree Sidebar */}
      <CollectionTree
        collections={collections}
        selectedCollection={selectedCollection}
        onSelectCollection={handleSelectCollection}
        onAddSubcollection={openAddSubcollectionDialog}
        onEditCollection={openEditCollectionDialog}
        onDeleteCollection={handleDeleteCollection}
        productCounts={products.reduce((acc, p) => {
          (p.collection_ids || []).forEach(cid => {
            acc[cid] = (acc[cid] || 0) + 1;
          });
          if (!p.collection_ids || p.collection_ids.length === 0) {
            acc['uncategorized'] = (acc['uncategorized'] || 0) + 1;
          }
          return acc;
        }, {})}
      />
      
      {/* Main Content */}
      <div className="flex-1">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-dashboard-button"
              variant="ghost"
              size="sm"
              className="rounded-full"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-outfit font-bold">Products</h1>
              <p className="text-muted-foreground">{store?.name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="settings-button"
              variant="outline"
              className="rounded-full"
              onClick={() => navigate(`/store/${storeId}/edit`)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button
              data-testid="analytics-button"
              variant="outline"
              className="rounded-full"
              onClick={() => navigate(`/store/${storeId}/analytics`)}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </Button>
            <Button
              data-testid="theme-button"
              variant="outline"
              className="rounded-full"
              onClick={() => navigate(`/store/${storeId}/theme`)}
            >
              <Palette className="w-4 h-4 mr-2" />
              Theme
            </Button>
            <Dialog open={collectionDialogOpen} onOpenChange={(open) => {
              setCollectionDialogOpen(open);
              if (!open) {
                setEditingCollection(null);
                setNewCollectionName('');
                setNewCollectionParent(null);
              }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-collection-button" variant="outline" className="rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Collection
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingCollection ? 'Edit Collection' : newCollectionParent ? 'Add Subcollection' : 'Add Collection'}</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <Label htmlFor="collection-name">Collection Name</Label>
                  <Input
                    id="collection-name"
                    data-testid="collection-name-input"
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                    placeholder="Electronics, Fashion, etc."
                  />
                </div>
                <DialogFooter>
                  <Button data-testid="submit-collection-button" onClick={handleAddCollection} className="rounded-full">
                    {editingCollection ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Dialog open={productDialogOpen} onOpenChange={(open) => {
              setProductDialogOpen(open);
              if (!open) resetProductForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-product-button" className="bg-primary text-primary-foreground rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Product URL *</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        data-testid="product-url-input"
                        value={productForm.original_url}
                        onChange={(e) => setProductForm({ ...productForm, original_url: e.target.value })}
                        placeholder="https://amazon.com/product..."
                      />
                      <Button
                        data-testid="scrape-product-button"
                        onClick={handleScrapeProduct}
                        disabled={scraping}
                        variant="outline"
                      >
                        {scraping ? 'Scraping...' : <LinkIcon className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="product-title">Title *</Label>
                    <Input
                      id="product-title"
                      data-testid="product-title-input"
                      value={productForm.title}
                      onChange={(e) => setProductForm({ ...productForm, title: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="product-description">Description</Label>
                    <Textarea
                      id="product-description"
                      data-testid="product-description-input"
                      value={productForm.description}
                      onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="product-images">Product Images (one URL per line)</Label>
                    <Textarea
                      id="product-images"
                      data-testid="product-images-input"
                      value={productForm.images.join('\n')}
                      onChange={(e) => setProductForm({ ...productForm, images: e.target.value.split('\n').filter(url => url.trim()) })}
                      placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                      rows={4}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter image URLs (one per line). These will be used if scraping doesn't find images.
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="product-affiliate">Affiliate Link *</Label>
                    <Input
                      id="product-affiliate"
                      data-testid="product-affiliate-input"
                      value={productForm.affiliate_link}
                      onChange={(e) => setProductForm({ ...productForm, affiliate_link: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="product-recommendation">Your Recommendation</Label>
                    <Textarea
                      id="product-recommendation"
                      data-testid="product-recommendation-input"
                      value={productForm.recommendation}
                      onChange={(e) => setProductForm({ ...productForm, recommendation: e.target.value })}
                      placeholder="Why do you recommend this product?"
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="product-collections">Collections (select multiple)</Label>
                    <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg p-3 space-y-2">
                      {collections.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No collections yet. Create one above.</p>
                      ) : (
                        collections.map((col) => (
                          <div key={col.collection_id} className="flex items-center space-x-2" style={{paddingLeft: `${col.level * 16}px`}}>
                            <Checkbox
                              id={`col-${col.collection_id}`}
                              checked={productForm.collection_ids.includes(col.collection_id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setProductForm({
                                    ...productForm,
                                    collection_ids: [...productForm.collection_ids, col.collection_id]
                                  });
                                } else {
                                  setProductForm({
                                    ...productForm,
                                    collection_ids: productForm.collection_ids.filter(id => id !== col.collection_id)
                                  });
                                }
                              }}
                            />
                            <label
                              htmlFor={`col-${col.collection_id}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {col.name}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Products can appear in multiple collections
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    data-testid="submit-product-button"
                    onClick={handleSubmitProduct}
                    disabled={submitting}
                    className="rounded-full"
                  >
                    {submitting ? 'Saving...' : (editingProduct ? 'Update Product' : 'Add Product')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        
        {/* Products List */}
        {products.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-muted rounded-2xl mx-auto mb-6 flex items-center justify-center">
              <Package className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-outfit font-bold mb-2">No products yet</h3>
            <p className="text-muted-foreground">Add your first product to get started</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <div
                key={product.product_id}
                data-testid={`product-card-${product.product_id}`}
                className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-all"
              >
                {product.images && product.images.length > 0 && (
                  <img
                    src={product.images[0]}
                    alt={product.title}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-4">
                  <h3 className="font-outfit font-bold text-lg mb-2 line-clamp-2">{product.title}</h3>
                  {product.price && (
                    <div className="mb-2">
                      <p className="text-primary font-bold">{product.price}</p>
                      {product.price_last_checked && (
                        <p className="text-xs text-muted-foreground">
                          Price checked {new Date(product.price_last_checked).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                    {product.description || 'No description'}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`edit-product-button-${product.product_id}`}
                      size="sm"
                      variant="outline"
                      className="flex-1 rounded-full"
                      onClick={() => openEditDialog(product)}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      data-testid={`delete-product-button-${product.product_id}`}
                      size="sm"
                      variant="ghost"
                      className="rounded-full text-destructive"
                      onClick={() => handleDeleteProduct(product.product_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
